import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import { validatePassword } from '../utils/validation';
import Icon from './Icon';

/**
 * 비밀번호 보안 요구사항 체크리스트 (인라인 구현)
 */
const PasswordChecklist = ({ password }) => {
  const checks = useMemo(() => [
    { key: 'minLength', label: '8자 이상', valid: password.length >= 8 },
    { key: 'uppercase', label: '대문자 포함', valid: /[A-Z]/.test(password) },
    { key: 'lowercase', label: '소문자 포함', valid: /[a-z]/.test(password) },
    { key: 'number', label: '숫자 포함', valid: /\d/.test(password) },
    { key: 'special', label: '특수문자 포함 (@$!%*?&)', valid: /[@$!%*?&]/.test(password) },
  ], [password]);

  return (
    <div className="password-checklist" role="list" aria-label="비밀번호 보안 요구사항">
      {checks.map((check) => (
        <div
          key={check.key}
          className="password-checklist__item"
          role="listitem"
          aria-label={`${check.label}: ${check.valid ? '충족' : '미충족'}`}
        >
          <span
            className={`password-checklist__icon ${check.valid ? 'password-checklist__icon--valid' : ''}`}
          >
            {check.valid ? '\u2713' : '\u25CB'}
          </span>
          <span>{check.label}</span>
        </div>
      ))}
    </div>
  );
};

PasswordChecklist.propTypes = {
  password: PropTypes.string.isRequired,
};

/**
 * ResetPassword — 비밀번호 재설정 페이지
 *
 * URL 쿼리 파라미터에서 token을 추출하여 새 비밀번호를 설정하는 폼을 제공한다.
 * - token이 없으면 에러 메시지 표시
 * - 비밀번호 유효성 검사 + 일치 확인
 * - 성공 시 안내 메시지 + 로그인 이동 버튼
 * - 실패 시 에러 메시지 (만료된 토큰 등)
 */
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // token이 없는 경우 에러 화면
  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <Icon name="error_outline" size={48} />
          </div>
          <h1 className="auth-title">잘못된 접근</h1>
          <p className="auth-subtitle auth-message">
            비밀번호 재설정 링크가 유효하지 않습니다. 이메일의 링크를 다시 확인해주세요.
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="btn-primary btn-full-width"
          >
            비밀번호 재설정 다시 요청
          </button>
        </div>
      </div>
    );
  }

  // 성공 화면
  if (isSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <Icon name="check_circle" size={48} color="var(--color-success)" />
          </div>
          <h1 className="auth-title">비밀번호 재설정 완료</h1>
          <p className="auth-subtitle auth-message">
            비밀번호가 재설정되었습니다. 새 비밀번호로 로그인해주세요.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary btn-full-width"
          >
            로그인하기
          </button>
        </div>
      </div>
    );
  }

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    const validationError = validatePassword(value);
    setPasswordError(validationError);

    // 비밀번호 확인 필드가 이미 입력된 경우 일치 여부 재검사
    if (confirmPassword && value !== confirmPassword) {
      setConfirmError('비밀번호가 일치하지 않습니다.');
    } else {
      setConfirmError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value && value !== password) {
      setConfirmError('비밀번호가 일치하지 않습니다.');
    } else {
      setConfirmError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 유효성 검사
    const validationError = validatePassword(password);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsLoading(true);

    try {
      await authService.resetPassword(token, password);
      setIsSuccess(true);
    } catch (err) {
      setError(err.userMessage || '비밀번호 재설정에 실패했습니다. 링크가 만료되었을 수 있습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">
            <Icon name="lock_reset" /> 새 비밀번호 설정
          </h1>
          <p className="auth-subtitle">
            새로운 비밀번호를 입력해주세요
          </p>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="reset-password">
              새 비밀번호
            </label>
            <input
              id="reset-password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              required
              className={passwordError ? 'has-error' : ''}
              placeholder="새 비밀번호를 입력하세요"
              autoComplete="new-password"
            />
            {password && (
              <PasswordChecklist password={password} />
            )}
            {passwordError && (
              <div className="form-error">
                {passwordError}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="reset-confirm-password">
              비밀번호 확인
            </label>
            <input
              id="reset-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              required
              className={confirmError ? 'has-error' : ''}
              placeholder="비밀번호를 다시 입력하세요"
              autoComplete="new-password"
            />
            {confirmError && (
              <div className="form-error">
                {confirmError}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !!passwordError || !!confirmError}
            className="btn-primary btn-full-width"
          >
            {isLoading && (
              <span className="loading-spinner"></span>
            )}
            {isLoading ? '처리 중...' : '비밀번호 재설정'}
          </button>

          <div className="auth-footer">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="btn-secondary btn-full-width"
            >
              로그인으로 돌아가기
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
